/**
 * Auth Cookie — Cross-origin session sharing
 *
 * Writes a cookie that the VitePress docs site can read to pick up
 * the user's authenticated session from the main React app.
 * Cookies are shared across ports on the same domain, unlike localStorage.
 */

const COOKIE_NAME = 'docurious-session'
const MAX_AGE_SECONDS = 7 * 24 * 60 * 60 // 7 days (matches refresh token)

interface CookiePayload {
  accessToken: string
  refreshToken: string
  expiresAt: string
  displayName: string
}

/**
 * Write auth tokens + display name to a cookie for cross-origin sharing.
 */
export function setAuthCookie(
  tokens: { accessToken: string; refreshToken: string; expiresAt: string },
  displayName: string,
): void {
  const payload: CookiePayload = {
    accessToken: tokens.accessToken,
    refreshToken: tokens.refreshToken,
    expiresAt: tokens.expiresAt,
    displayName,
  }

  const isSecure = window.location.protocol === 'https:'
  const parts = [
    `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(payload))}`,
    `path=/`,
    `max-age=${MAX_AGE_SECONDS}`,
    `SameSite=Lax`,
  ]
  if (isSecure) parts.push('Secure')

  document.cookie = parts.join('; ')
}

/**
 * Remove the auth cookie (on logout).
 */
export function clearAuthCookie(): void {
  document.cookie = `${COOKIE_NAME}=; path=/; max-age=0; SameSite=Lax`
}

/**
 * Read the auth cookie. Used by httpClient during token refresh
 * to preserve the displayName when updating tokens.
 */
export function readAuthCookie(): CookiePayload | null {
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${COOKIE_NAME}=`))

  if (!match) return null

  try {
    return JSON.parse(decodeURIComponent(match.split('=').slice(1).join('=')))
  } catch {
    return null
  }
}
